import { useCallback, useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DeptIcon } from "@/components/DeptIcon";
import { LockNotice, PANEL_POLICY } from "@/components/LockNotice";
import { BookingFlow } from "@/components/panels/BookingFlow";
import { HomeCareForm } from "@/components/panels/HomeCareForm";
import { RequestsPanel } from "@/components/panels/RequestsPanel";
import { DocumentsPanel } from "@/components/panels/DocumentsPanel";
import { AppointmentList } from "@/components/AppointmentList";
import { OperatorAppointments } from "@/components/operator/OperatorAppointments";
import { Requests, SharedDocs } from "@/pages/StaffPage";

const STAFF = ["medico", "infermiere", "segreteria", "admin", "super_admin"];
const OP_KIND = { telemedicina: "video", agenda: "visita", domiciliare: "domicilio" };

function OperatorBody({ dep, user }) {
  const staff = STAFF.includes(user.role);
  if (OP_KIND[dep.panel_type]) return <OperatorAppointments kind={OP_KIND[dep.panel_type]} />;
  if (dep.panel_type === "richieste" || dep.panel_type === "documenti") {
    if (!staff) return <p className="text-sm text-slate-500" data-testid="operator-staff-only">Questa sezione è riservata a medici, infermieri e segreteria della clinica.</p>;
    return dep.panel_type === "richieste" ? <Requests isDoctor={user.role === "medico"} /> : <SharedDocs />;
  }
  return null;
}

function OperatorTabs({ dep, user, children }) {
  const body = <OperatorBody dep={dep} user={user} />;
  if (!OP_KIND[dep.panel_type] && !["richieste", "documenti"].includes(dep.panel_type)) return children;
  return (
    <Tabs defaultValue="op">
      <TabsList className="mb-4 w-full">
        <TabsTrigger value="op" className="flex-1" data-testid="dept-tab-operator">Gestione operatore</TabsTrigger>
        <TabsTrigger value="user" className="flex-1" data-testid="dept-tab-user-view">Vista utente</TabsTrigger>
      </TabsList>
      <TabsContent value="op" data-testid="operator-section">{body}</TabsContent>
      <TabsContent value="user">{children}</TabsContent>
    </Tabs>
  );
}

function MyAppointments({ kind }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/appointments/mine").then(({ data }) => setItems(data.filter((a) => !kind || a.kind === kind))), [kind]);
  useEffect(() => {
    load();
  }, [load]);
  return <AppointmentList items={items} reload={load} />;
}

function WithHistory({ kind, children }) {
  const { user } = useAuth();
  if (!user) return children;
  return (
    <Tabs defaultValue="new">
      <TabsList className="mb-4 w-full">
        <TabsTrigger value="new" className="flex-1" data-testid="dept-tab-new">Nuova</TabsTrigger>
        <TabsTrigger value="mine" className="flex-1" data-testid="dept-tab-history">Storico</TabsTrigger>
      </TabsList>
      <TabsContent value="new">{children}</TabsContent>
      <TabsContent value="mine"><MyAppointments kind={kind} /></TabsContent>
    </Tabs>
  );
}

function PanelBody({ dep, onOpenChat }) {
  switch (dep.panel_type) {
    case "telemedicina": return <WithHistory kind="video"><BookingFlow kind="video" /></WithHistory>;
    case "agenda": return <WithHistory><BookingFlow kind="visita" /></WithHistory>;
    case "domiciliare": return <WithHistory kind="domicilio"><HomeCareForm /></WithHistory>;
    case "richieste": return <RequestsPanel />;
    case "documenti": return <DocumentsPanel />;
    case "ai":
      return (
        <div className="space-y-4 text-sm text-slate-600">
          <p>Doctor Selvix ti orienta nei reparti, spiega procedure e documenti e fornisce informazioni amministrative. Non effettua diagnosi.</p>
          <Button data-testid="dept-open-chat-btn" onClick={onOpenChat} className="w-full rounded-full bg-sky-600 hover:bg-sky-700"><MessageCircle className="mr-2 h-4 w-4" /> Parla con Doctor Selvix</Button>
        </div>
      );
    default: return <p className="text-sm text-slate-600">{dep.description}</p>;
  }
}

export function DepartmentSheet({ dep, onClose, onOpenChat }) {
  const { user, access } = useAuth();
  const policy = dep && access?.policies?.[PANEL_POLICY[dep.panel_type]];
  const locked = user && policy && !policy.allowed;
  const isOperator = user && (STAFF.includes(user.role) || access?.levels?.L3);
  const body = locked ? <LockNotice policy={policy} /> : <PanelBody dep={dep} onOpenChat={onOpenChat} />;
  return (
    <Sheet open={!!dep} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l border-cyan-200 bg-white/95 backdrop-blur-xl sm:max-w-xl" data-testid="department-sheet">
        {dep && (
          <>
            <SheetHeader className="mb-6 text-left">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sky-600 text-white shadow-lg shadow-cyan-500/30"><DeptIcon name={dep.icon} className="h-5 w-5" /></span>
              <SheetTitle className="font-display text-2xl font-extrabold text-slate-900" data-testid="department-sheet-title">{dep.title}</SheetTitle>
              <SheetDescription>{dep.description}</SheetDescription>
            </SheetHeader>
            {isOperator ? <OperatorTabs dep={dep} user={user}>{body}</OperatorTabs> : body}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
